import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";
import {
  ContractOverviewResponse,
  ContractAnalyticsResponse,
} from "../types/contracts";
import {
  SupplierOverviewResponse,
  SupplierWithMostContractsResponse,
  SuppliersDirectoryApiResponse,
} from "../types/suppliers";
import { SourcingOverviewResponse, SourcingPerformanceResponse, TeamLeaderboardResponse } from "../types/sourcing";

// Async thunk to fetch both contract overview and analytics in parallel
export const fetchAllContractData = createAsyncThunk<
  {
    overview: ContractOverviewResponse;
    analytics: ContractAnalyticsResponse;
  },
  {
    group_company?: string[];
    contract_owner?: string[];
    start_date_with_sd?: string;
    end_date_with_sd?: string;
    start_date_with_ed?: string;
    end_date_with_ed?: string;
    start_date_with_cct_created_at?: string;
    end_date_with_cct_created_at?: string;
    start_date_with_cct_updated_at?: string;
    end_date_with_cct_updated_at?: string;
  } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchAllContractData",
  async (params, { rejectWithValue }) => {
    try {
      const filters: any = {};
      if (params && 'group_company' in params && params.group_company && params.group_company.length > 0) {
        filters.group_company = params.group_company;
      }
      if (params && 'contract_owner' in params && params.contract_owner && params.contract_owner.length > 0) {
        filters.contract_owner = params.contract_owner;
      }
      if (params && 'start_date_with_sd' in params && params.start_date_with_sd) {
        filters.start_date_with_sd = params.start_date_with_sd;
      }
      if (params && 'end_date_with_sd' in params && params.end_date_with_sd) {
        filters.end_date_with_sd = params.end_date_with_sd;
      }
      if (params && 'start_date_with_ed' in params && params.start_date_with_ed) {
        filters.start_date_with_ed = params.start_date_with_ed;
      }
      if (params && 'end_date_with_ed' in params && params.end_date_with_ed) {
        filters.end_date_with_ed = params.end_date_with_ed;
      }
      if (params && 'start_date_with_cct_created_at' in params && params.start_date_with_cct_created_at) {
        filters.start_date_with_cct_created_at = params.start_date_with_cct_created_at;
      }
      if (params && 'end_date_with_cct_created_at' in params && params.end_date_with_cct_created_at) {
        filters.end_date_with_cct_created_at = params.end_date_with_cct_created_at;
      }
      if (params && 'start_date_with_cct_updated_at' in params && params.start_date_with_cct_updated_at) {
        filters.start_date_with_cct_updated_at = params.start_date_with_cct_updated_at;
      }
      if (params && 'end_date_with_cct_updated_at' in params && params.end_date_with_cct_updated_at) {
        filters.end_date_with_cct_updated_at = params.end_date_with_cct_updated_at;
      }

      const [overviewResponse, analyticsResponse] = await Promise.all([
        axiosClient.post<ContractOverviewResponse>("/contract-overview", filters),
        axiosClient.post<ContractAnalyticsResponse>("/contract-analytics", filters),
      ]);

      return {
        overview: overviewResponse.data,
        analytics: analyticsResponse.data,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch contract data"
      );
    }
  }
);

// Async thunk to fetch all supplier data in parallel (overview, most contracts, and directory)
export const fetchAllSupplierData = createAsyncThunk<
  {
    overview: SupplierOverviewResponse;
    suppliersWithMostContracts: SupplierWithMostContractsResponse;
    suppliersDirectory: SuppliersDirectoryApiResponse;
  },
  { limit?: number; offset?: number; search?: string; start_date?: string; end_date?: string } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchAllSupplierData",
  async (params, { rejectWithValue }) => {
    try {
      const limit = params && 'limit' in params ? params.limit : 10;
      const offset = params && 'offset' in params ? params.offset : 0;
      const search = params && 'search' in params ? params.search : '';
      const start_date = params && 'start_date' in params ? params.start_date : '';
      const end_date = params && 'end_date' in params ? params.end_date : '';

      const directoryUrl = `/suppliers-directory?limit=${limit}&offset=${offset}${search ? `&search=${encodeURIComponent(search)}` : ''}`;

      const filters: any = {};
      if (start_date) {
        filters.start_date = start_date;
      }
      if (end_date) {
        filters.end_date = end_date;
      }

      const [overviewResponse, suppliersWithMostContractsResponse, directoryResponse] = await Promise.all([
        axiosClient.post<SupplierOverviewResponse>("/supplier-overview", filters),
        axiosClient.post<SupplierWithMostContractsResponse>("/supplier-with-most-contracts", filters),
        axiosClient.post<SuppliersDirectoryApiResponse>(directoryUrl, filters),
      ]);

      return {
        overview: overviewResponse.data,
        suppliersWithMostContracts: suppliersWithMostContractsResponse.data,
        suppliersDirectory: directoryResponse.data,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch supplier data"
      );
    }
  }
);

// Async thunk to fetch only suppliers directory with pagination (for pagination updates)
export const fetchSuppliersDirectory = createAsyncThunk<
  SuppliersDirectoryApiResponse,
  { limit: number; offset: number; search?: string; start_date?: string; end_date?: string },
  { rejectValue: string }
>(
  "procurementDashboard/fetchSuppliersDirectory",
  async ({ limit, offset, search, start_date, end_date }, { rejectWithValue }) => {
    try {
      const url = `/suppliers-directory?limit=${limit}&offset=${offset}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
      const filters: any = {};
      if (start_date) {
        filters.start_date = start_date;
      }
      if (end_date) {
        filters.end_date = end_date;
      }
      const response = await axiosClient.post<SuppliersDirectoryApiResponse>(url, filters);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch suppliers directory"
      );
    }
  }
);

// Async thunk to fetch sourcing overview data
export const fetchSourcingOverview = createAsyncThunk<
  SourcingOverviewResponse,
  { entity?: string[]; start_date?: string; end_date?: string } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchSourcingOverview",
  async (params, { rejectWithValue }) => {
    try {
      const filters: any = {};
      if (params && 'entity' in params && params.entity && params.entity.length > 0) {
        filters.entity = params.entity;
      }
      if (params && 'start_date' in params && params.start_date) {
        filters.start_date = params.start_date;
      }
      if (params && 'end_date' in params && params.end_date) {
        filters.end_date = params.end_date;
      }

      const response = await axiosClient.post<SourcingOverviewResponse>(
        "/sourcing-overview",
        filters
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing overview data"
      );
    }
  }
);

// Async thunk to fetch sourcing performance data
export const fetchSourcingPerformance = createAsyncThunk<
  SourcingPerformanceResponse,
  { entity?: string[]; start_date?: string; end_date?: string } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchSourcingPerformance",
  async (params, { rejectWithValue }) => {
    try {
      const filters: any = {};
      if (params && 'entity' in params && params.entity && params.entity.length > 0) {
        filters.entity = params.entity;
      }
      if (params && 'start_date' in params && params.start_date) {
        filters.start_date = params.start_date;
      }
      if (params && 'end_date' in params && params.end_date) {
        filters.end_date = params.end_date;
      }

      const response = await axiosClient.post<SourcingPerformanceResponse>(
        "/sourcing-performance",
        filters
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing performance data"
      );
    }
  }
);

// Async thunk to fetch team leaderboard data
export const fetchTeamLeaderboard = createAsyncThunk<
  TeamLeaderboardResponse,
  { entity?: string[]; start_date?: string; end_date?: string; sort_by?: string } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchTeamLeaderboard",
  async (params, { rejectWithValue }) => {
    try {
      const filters: any = {
        sort_by: params && 'sort_by' in params && params.sort_by ? params.sort_by : "total_pos",
      };
      if (params && 'entity' in params && params.entity && params.entity.length > 0) {
        filters.entity = params.entity;
      }
      if (params && 'start_date' in params && params.start_date) {
        filters.start_date = params.start_date;
      }
      if (params && 'end_date' in params && params.end_date) {
        filters.end_date = params.end_date;
      }

      const response = await axiosClient.post<TeamLeaderboardResponse>(
        "/sourcing-performance-teamleaderboard",
        filters
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch team leaderboard data"
      );
    }
  }
);

export interface FilterState {
  group_company?: string[];
  contract_owner?: string[];
  start_date_with_sd?: string;
  end_date_with_sd?: string;
  start_date_with_ed?: string;
  end_date_with_ed?: string;
  start_date_with_cct_created_at?: string;
  end_date_with_cct_created_at?: string;
  start_date_with_cct_updated_at?: string;
  end_date_with_cct_updated_at?: string;
  start_date?: string;
  end_date?: string;
  entity?: string[];
}

interface ProcurementDashboardState {
  contractOverview: ContractOverviewResponse | null;
  contractAnalytics: ContractAnalyticsResponse | null;
  supplierOverview: SupplierOverviewResponse | null;
  suppliersWithMostContracts: SupplierWithMostContractsResponse | null;
  suppliersDirectory: SuppliersDirectoryApiResponse | null;
  sourcingOverview: SourcingOverviewResponse | null;
  sourcingPerformance: SourcingPerformanceResponse | null;
  teamLeaderboard: TeamLeaderboardResponse | null;
  contractsLoading: boolean;
  contractsError: string | null;
  suppliersLoading: boolean;
  suppliersError: string | null;
  sourcingLoading: boolean;
  sourcingError: string | null;
  sourcingPerformanceLoading: boolean;
  sourcingPerformanceError: string | null;
  teamLeaderboardLoading: boolean;
  teamLeaderboardError: string | null;
  lastContractsFetched: number | null;
  lastSuppliersFetched: number | null;
  lastSourcingFetched: number | null;
  lastSourcingPerformanceFetched: number | null;
  lastTeamLeaderboardFetched: number | null;
  contractsFilters: FilterState;
  suppliersFilters: FilterState;
  sourcingFilters: FilterState;
}

const initialState: ProcurementDashboardState = {
  contractOverview: null,
  contractAnalytics: null,
  supplierOverview: null,
  suppliersWithMostContracts: null,
  suppliersDirectory: null,
  sourcingOverview: null,
  sourcingPerformance: null,
  teamLeaderboard: null,
  contractsLoading: false,
  contractsError: null,
  suppliersLoading: false,
  suppliersError: null,
  sourcingLoading: false,
  sourcingError: null,
  sourcingPerformanceLoading: false,
  sourcingPerformanceError: null,
  teamLeaderboardLoading: false,
  teamLeaderboardError: null,
  lastContractsFetched: null,
  lastSuppliersFetched: null,
  lastSourcingFetched: null,
  lastSourcingPerformanceFetched: null,
  lastTeamLeaderboardFetched: null,
  contractsFilters: {},
  suppliersFilters: {},
  sourcingFilters: {},
};

const procurementDashboardSlice = createSlice({
  name: "procurementDashboard",
  initialState,
  reducers: {
    setContractsFilters: (state, action: { payload: FilterState }) => {
      state.contractsFilters = action.payload;
    },
    setSuppliersFilters: (state, action: { payload: FilterState }) => {
      state.suppliersFilters = action.payload;
    },
    setSourcingFilters: (state, action: { payload: FilterState }) => {
      state.sourcingFilters = action.payload;
    },
    clearContractsError: (state) => {
      state.contractsError = null;
    },
    clearSuppliersError: (state) => {
      state.suppliersError = null;
    },
    clearSourcingError: (state) => {
      state.sourcingError = null;
    },
    clearSourcingPerformanceError: (state) => {
      state.sourcingPerformanceError = null;
    },
    clearTeamLeaderboardError: (state) => {
      state.teamLeaderboardError = null;
    },
    clearAllErrors: (state) => {
      state.contractsError = null;
      state.suppliersError = null;
      state.sourcingError = null;
      state.sourcingPerformanceError = null;
      state.teamLeaderboardError = null;
    },
    clearContractData: (state) => {
      state.contractOverview = null;
      state.contractAnalytics = null;
      state.contractsError = null;
      state.contractsLoading = false;
      state.lastContractsFetched = null;
    },
    clearSupplierData: (state) => {
      state.supplierOverview = null;
      state.suppliersWithMostContracts = null;
      state.suppliersDirectory = null;
      state.suppliersError = null;
      state.suppliersLoading = false;
      state.lastSuppliersFetched = null;
    },
    clearSourcingData: (state) => {
      state.sourcingOverview = null;
      state.sourcingPerformance = null;
      state.teamLeaderboard = null;
      state.sourcingError = null;
      state.sourcingLoading = false;
      state.sourcingPerformanceError = null;
      state.sourcingPerformanceLoading = false;
      state.teamLeaderboardError = null;
      state.teamLeaderboardLoading = false;
      state.lastSourcingFetched = null;
      state.lastSourcingPerformanceFetched = null;
      state.lastTeamLeaderboardFetched = null;
    },
    clearAllData: (state) => {
      state.contractOverview = null;
      state.contractAnalytics = null;
      state.supplierOverview = null;
      state.suppliersWithMostContracts = null;
      state.suppliersDirectory = null;
      state.sourcingOverview = null;
      state.sourcingPerformance = null;
      state.teamLeaderboard = null;
      state.contractsError = null;
      state.contractsLoading = false;
      state.suppliersError = null;
      state.suppliersLoading = false;
      state.sourcingError = null;
      state.sourcingLoading = false;
      state.sourcingPerformanceError = null;
      state.sourcingPerformanceLoading = false;
      state.teamLeaderboardError = null;
      state.teamLeaderboardLoading = false;
      state.lastContractsFetched = null;
      state.lastSuppliersFetched = null;
      state.lastSourcingFetched = null;
      state.lastSourcingPerformanceFetched = null;
      state.lastTeamLeaderboardFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContractData.pending, (state) => {
        state.contractsLoading = true;
        state.contractsError = null;
      })
      .addCase(fetchAllContractData.fulfilled, (state, action) => {
        state.contractsLoading = false;
        state.contractOverview = action.payload.overview;
        state.contractAnalytics = action.payload.analytics;
        state.contractsError = null;
        state.lastContractsFetched = Date.now();
      })
      .addCase(fetchAllContractData.rejected, (state, action) => {
        state.contractsLoading = false;
        state.contractsError = action.payload || "Failed to fetch contract data";
      })
      .addCase(fetchAllSupplierData.pending, (state) => {
        state.suppliersLoading = true;
        state.suppliersError = null;
      })
      .addCase(fetchAllSupplierData.fulfilled, (state, action) => {
        state.suppliersLoading = false;
        state.supplierOverview = action.payload.overview;
        state.suppliersWithMostContracts = action.payload.suppliersWithMostContracts;
        state.suppliersDirectory = action.payload.suppliersDirectory;
        state.suppliersError = null;
        state.lastSuppliersFetched = Date.now();
      })
      .addCase(fetchAllSupplierData.rejected, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersError = action.payload || "Failed to fetch supplier data";
      })
      .addCase(fetchSuppliersDirectory.pending, (state) => {
        state.suppliersLoading = true;
      })
      .addCase(fetchSuppliersDirectory.fulfilled, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersDirectory = action.payload;
        state.suppliersError = null;
      })
      .addCase(fetchSuppliersDirectory.rejected, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersError = action.payload || "Failed to fetch suppliers directory";
      })
      .addCase(fetchSourcingOverview.pending, (state) => {
        state.sourcingLoading = true;
        state.sourcingError = null;
      })
      .addCase(fetchSourcingOverview.fulfilled, (state, action) => {
        state.sourcingLoading = false;
        state.sourcingOverview = action.payload;
        state.sourcingError = null;
        state.lastSourcingFetched = Date.now();
      })
      .addCase(fetchSourcingOverview.rejected, (state, action) => {
        state.sourcingLoading = false;
        state.sourcingError = action.payload || "Failed to fetch sourcing overview data";
      })
      .addCase(fetchSourcingPerformance.pending, (state) => {
        state.sourcingPerformanceLoading = true;
        state.sourcingPerformanceError = null;
      })
      .addCase(fetchSourcingPerformance.fulfilled, (state, action) => {
        state.sourcingPerformanceLoading = false;
        state.sourcingPerformance = action.payload;
        state.sourcingPerformanceError = null;
        state.lastSourcingPerformanceFetched = Date.now();
      })
      .addCase(fetchSourcingPerformance.rejected, (state, action) => {
        state.sourcingPerformanceLoading = false;
        state.sourcingPerformanceError = action.payload || "Failed to fetch sourcing performance data";
      })
      .addCase(fetchTeamLeaderboard.pending, (state) => {
        state.teamLeaderboardLoading = true;
        state.teamLeaderboardError = null;
      })
      .addCase(fetchTeamLeaderboard.fulfilled, (state, action) => {
        state.teamLeaderboardLoading = false;
        state.teamLeaderboard = action.payload;
        state.teamLeaderboardError = null;
        state.lastTeamLeaderboardFetched = Date.now();
      })
      .addCase(fetchTeamLeaderboard.rejected, (state, action) => {
        state.teamLeaderboardLoading = false;
        state.teamLeaderboardError = action.payload || "Failed to fetch team leaderboard data";
      });
  },
});

export const {
  setContractsFilters,
  setSuppliersFilters,
  setSourcingFilters,
  clearContractsError,
  clearSuppliersError,
  clearSourcingError,
  clearSourcingPerformanceError,
  clearTeamLeaderboardError,
  clearAllErrors,
  clearContractData,
  clearSupplierData,
  clearSourcingData,
  clearAllData,
} = procurementDashboardSlice.actions;
export default procurementDashboardSlice.reducer;
